import { ITransfer, TransferTarget, TransferType } from '@agent/core/contact/transfer/models/transfer';
import { ITransferDeprecatedService } from '@agent/core/contact/transfer/transfer-deprecated.service';
import { IDirectoryService } from '@agent/core/search/directory.service';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { RxUtil } from '@commons/util/utilities/rx';
import { ILogService, IPromise, IQService, isArray, isDefined } from 'angular';
import { i18n } from 'i18next';
import { first, map, Observable } from 'rxjs';

import { Configuration, CorrespondentElsQuery, ElasticSearchService, Entity, None, Option, ToasterFacade } from '@contact/upgrade';

const MODULE_NAME = 'phoneTransferActionsService';

type FilterType = 'group' | 'team' | 'skill';
export type AgentFilterType = undefined | 'teamOrGroup' | 'skill';

interface IAgentFilter extends Entity {
  id: string;
  name: string;
  type: FilterType;
  skills?: IAgentFilter[];
  agentAvailable?: boolean;
}

interface IAgentFilterChosen extends IAgentFilter {
  chosen: boolean;
  selected: boolean;
}

export interface IPhoneTransferActionsService {
  validate(responseId: string): void;
  getEnableValidateBtn(): boolean;
  setEnableValidateBtn(val: boolean): void;
  getNoResultsAgentTarget(): boolean;
  setNoResultsAgentTarget(val: boolean): void;
  getResultsAgentTarget(filter: string): IPromise<any>;
  setResultsAgentTarget(val: IPromise<any>): void;
  getAgents(filterType?: AgentFilterType, text?: string, channelType?: string, channelDirection?: string): IPromise<any>;
  getSkillsFilter(): IAgentFilterChosen[];
  setSkillsFilter(val: IAgentFilterChosen[]): void;
  setTeamOrGroupFilter(val: IAgentFilter): void;
  getTeamOrGroupFilter(): IAgentFilter;
  getAgentStatus(csrId: string): Observable<any>;
  getIsCurrentAgentAvailable(): boolean;
  setIsCurrentAgentAvailable(val: boolean): void;
  getPhoneInputPattern(): string;
  getCheckAvailabilityWithoutMediaBlending(): boolean;
  setChannelType(val: string): void;
  getChannelType(): string;
  setChannelDirection(val: string): void;
  getChannelDirection(): string;
}

export class PhoneTransferActionsService implements IPhoneTransferActionsService {
  static $inject: Array<string> = [
    '$log',
    '$i18next',
    '$q',
    'toastDeprecatedService',
    'transferDeprecatedService',
    'directory',
    'sessionDeprecatedService',
    'toasterFacade',
    'elasticSearchService',
    'configuration',
  ];

  public name = MODULE_NAME;

  protected enableValidateBtn = true;
  protected noResultsAgentTarget: boolean = false;
  protected resultsAgentTarget: IPromise<any>;
  protected skillsFilter: IAgentFilterChosen[] = [];
  protected teamOrGroupFilter: IAgentFilter;
  protected isCurrentAgentAvailable: boolean = true;
  protected phoneInputPattern: string;
  protected checkAvailabilityWithoutMediaBlending: boolean;
  protected channelType: string;
  protected channelDirection: string;

  constructor(
    protected readonly $log: ILogService,
    protected readonly $i18next: i18n,
    protected readonly $q: IQService,
    protected readonly toastDeprecatedService: ToastDeprecatedService,
    protected readonly transferDeprecatedService: ITransferDeprecatedService,
    protected readonly directoryService: IDirectoryService,
    protected readonly sessionDeprecatedService: SessionDeprecatedService,
    protected readonly toasterFacade: ToasterFacade,
    protected readonly elasticSearchService: ElasticSearchService,
    protected readonly configuration: Configuration
  ) {
    this.phoneInputPattern = this.configuration.phoneInputPattern || '';
    if (!!this.configuration.checkAvailabilityWithoutMediaBlending) {
      this.checkAvailabilityWithoutMediaBlending = this.configuration.checkAvailabilityWithoutMediaBlending;
    } else {
      this.checkAvailabilityWithoutMediaBlending = false;
    }
  }

  getEnableValidateBtn(): boolean {
    return this.enableValidateBtn;
  }

  setEnableValidateBtn(val: boolean): void {
    this.enableValidateBtn = val;
  }

  getNoResultsAgentTarget(): boolean {
    return this.noResultsAgentTarget;
  }

  setNoResultsAgentTarget(val: boolean): void {
    this.noResultsAgentTarget = val;
  }

  /**
   * used to put a string to lowercase and replace accented characters by their non accented version
   * @parameter inputValue the string to replace the characters in
   * @output return the inputValue string in lowercase with accented characters replaced by their non accented version
   */
  private static normalizeString = (inputValue: string): string => {
    // transform a string to lowercase and replace 'àé...' to 'ae...'
    return inputValue
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  getResultsAgentTarget(filter: string): IPromise<any> {
    return this.resultsAgentTarget.then(agents => {
      let copy: string[] = [];
      //normalize the agent name and the filter before filtering so the research is case and accent insensitive
      let normalizedFilter = PhoneTransferActionsService.normalizeString(filter);
      agents.forEach(function (agent) {
        let normalizedName = PhoneTransferActionsService.normalizeString(agent.name);
        if (normalizedName.indexOf(normalizedFilter) !== -1) {
          copy.push(agent);
        }
      });
      return copy;
    });
  }

  setResultsAgentTarget(val: IPromise<any>): void {
    this.resultsAgentTarget = val;
  }

  getSkillsFilter(): IAgentFilterChosen[] {
    return this.skillsFilter;
  }

  getTeamOrGroupFilter(): IAgentFilter {
    return this.teamOrGroupFilter;
  }

  setSkillsFilter(val: IAgentFilterChosen[]): void {
    this.skillsFilter = val;
  }

  setTeamOrGroupFilter(val: IAgentFilter): void {
    this.teamOrGroupFilter = val;
  }

  getIsCurrentAgentAvailable(): boolean {
    return this.isCurrentAgentAvailable;
  }

  setIsCurrentAgentAvailable(val: boolean): void {
    this.isCurrentAgentAvailable = val;
  }

  getPhoneInputPattern(): string {
    return this.phoneInputPattern;
  }

  getCheckAvailabilityWithoutMediaBlending(): boolean {
    return this.checkAvailabilityWithoutMediaBlending;
  }

  setChannelType(val: string): void {
    this.channelType = val;
  }

  getChannelType(): string {
    return this.channelType;
  }

  setChannelDirection(val: string): void {
    this.channelDirection = val;
  }

  getChannelDirection(): string {
    return this.channelDirection;
  }

  getAgents(filterType?: AgentFilterType, text?: string, channelType?: string, channelDirection?: string): IPromise<any> {
    let groupFilter = None;
    let teamFilter = None;

    if ((!filterType || filterType === 'teamOrGroup') && this.skillsFilter.length === 0) {
      let filterTeamGroup = Option.of(this.teamOrGroupFilter);
      groupFilter = filterTeamGroup.filter(f => f.type === 'group');
      teamFilter = filterTeamGroup.filter(f => f.type === 'team');
    }

    return this.directoryService
      .getInternalCorrespondents(this.sessionDeprecatedService.getProfile().domainId, groupFilter, teamFilter, this.skillsFilter, text)
      .then(success => {
        if (success.data.length !== 0) {
          if (channelType !== undefined && channelDirection !== undefined) {
            return this.getAgentWithAvailableSkillsForAGivenChannel(success.data, channelType, channelDirection).then(users => {
              if (!!users && users.length !== 0) {
                return users;
              } else {
                this.noResultsAgentTarget = true;
                return [];
              }
            });
          } else {
            return success.data.map((agent: any) => ({
              id: agent.uuid,
              name: `${agent.firstName} ${agent.lastName}`,
              phoneNumber: agent.phoneNumber,
            }));
          }
        } else {
          this.noResultsAgentTarget = true;
          return [];
        }
      });
  }

  private buildAgent(_source: any, phoneNumber: string, channelType: string, channelDirection: string): any {
    return {
      id: _source.uuid,
      name: `${_source.firstName} ${_source.lastName}`,
      phoneNumber: phoneNumber,
      skills: _source.skills
        .filter((skill: any) => skill.mediaDirection === channelDirection && skill.mediaType === channelType)
        .map((skill: any) => ({
          id: skill.uuid,
          name: skill.name,
        })),
    };
  }

  private getAgentWithAvailableSkillsForAGivenChannel(agents: any, channelType: string, channelDirection: string): IPromise<any> {
    const deferred = this.$q.defer<any>();
    const agentsUuids = agents.map((agent: any) => agent.uuid);

    const searchParams: any = {
      types: ['user'],
      query: {
        bool: {
          must: [
            {
              terms: { uuid: agentsUuids },
            },
          ],
        },
      },
    };

    RxUtil.toPromise(this.elasticSearchService.searchOnSeveralTypesDeprecated(searchParams), this.$q).then(
      success => {
        if (
          !!success &&
          isDefined(success.hits) &&
          isDefined(success.hits.total) &&
          isDefined(success.hits.total.value) &&
          success.hits.total.value > 0 &&
          isArray(success.hits.hits)
        ) {
          const agentsWithSkills = success.hits.hits.map(agent => {
            const phoneNumber = agents.filter(elt => elt.uuid === agent._source.uuid)[0].phoneNumber;
            return this.buildAgent(agent._source, phoneNumber, channelType, channelDirection);
          });

          // Remove the agents which don't have any skill
          const agentsWithSkillsFiltered = agentsWithSkills.filter((agent: any) => agent.skills.length > 0);

          // Check if the agents have the skills activated if we are filtering by skills
          if (this.skillsFilter.length > 0) {
            const skillsFilterUuid = this.skillsFilter.map((skill: any) => skill.id);
            deferred.resolve(
              agentsWithSkillsFiltered.filter((agent: any) => {
                const skillsUserUuid = agent.skills.map((skill: any) => skill.id);
                return skillsFilterUuid.some(uuid => skillsUserUuid.indexOf(uuid) >= 0);
              })
            );
          } else {
            deferred.resolve(agentsWithSkillsFiltered);
          }
        } else {
          deferred.reject('[phone-transfer] cannot find csrs');
        }
      },
      () => deferred.reject('[phone-transfer] Error on searchOnSeveralTypesWithPromise in getAgentWithAvailableSkillsForAGivenChannel')
    );

    return deferred.promise;
  }

  getAgentStatus(csrId: string): Observable<any> {
    return this.elasticSearchService
      .search(
        CorrespondentElsQuery.buildSearchById({
          id: csrId,
          sort: {
            'lastName.keyword': 'asc',
            'firstName.keyword': 'asc',
          },
        })
      )
      .pipe(
        first(),
        map(({ data }) =>
          data.map(({ uuid, csrStatus }) => ({
            id: uuid,
            csrStatus: csrStatus,
          }))
        )
      );
  }

  validate(responseId: string): void {
    this.transferDeprecatedService.getTransfer(responseId).map(transfert => {
      switch (transfert.targetType) {
        case TransferTarget.AGENT:
          if (!!transfert.target.name && !!transfert.target.id) {
            this.directoryService
              .getCorrespondentByCsrUuid(
                transfert.target.id,
                this.sessionDeprecatedService.getProfile().domainId,
                this.checkAvailabilityWithoutMediaBlending
              )
              .then(success => {
                if (success.data.length !== 0) {
                  this.performValidate(responseId, transfert);
                } else {
                  let name: string = transfert.target.name.slice();
                  this.toastDeprecatedService.alert(
                    this.$i18next.t('agent:contact.workspace.phone.transfer.agent_no_always_available_toast', { agent: name }),
                    5000,
                    this.$i18next.t('agent:toast.title_transfert')
                  );

                  transfert.target = null;
                  this.enableValidateBtn = true;

                  this.noResultsAgentTarget = false;
                  this.resultsAgentTarget = this.getAgents(undefined, this.channelType, this.channelDirection);
                  this.isCurrentAgentAvailable = true;
                }
              });
          } else {
            this.performValidate(responseId, transfert);
          }
          break;
        default:
          this.performValidate(responseId, transfert);
      }
    });
  }

  private performValidate(responseId: string, transfer: ITransfer): void {
    this.transferDeprecatedService.validateTransfer(responseId).then(
      response => {
        if (!response || response.status !== 200) {
          // if error in http call reactivate validate button
          this.enableValidateBtn = true;
          this.toasterFacade.notification({
            message: 'agent:contact.workspace.phone.transfer.toast.error',
          });
        } else {
          // if transfer is not accompanied and call is ok reset button flag
          if (transfer.type === TransferType.NOT_ACCOMPANIED) {
            this.enableValidateBtn = true;
          }
          this.$log.debug('[contactPhoneTransferActions]Transfer request sent');
        }
      },
      () => {
        // if error in http call reactivate validate button
        this.enableValidateBtn = true;
        this.toasterFacade.notification({
          message: 'agent:contact.workspace.phone.transfer.toast.error',
        });
      }
    );
  }
}

export default MODULE_NAME;
